import React, { useEffect, useState } from "react";
import { FragmentScheduleForm } from "../fragments.hrm/FragmentScheduleForm";
import {
    getScheduleApi,
    deleteScheduleApi,
} from "../../../apis/apis.hrm/apisSchedule";
import { WidgetModal } from "../../modules.widgets";
import swal from "sweetalert2";

export function PageSchedule() {
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);

    const loadSchedules = async () => {
        setLoading(true);
        const res = await getScheduleApi();
        setSchedules(res);
        setLoading(false);
    };

    useEffect(() => {
        loadSchedules();
    }, []);

    const deleteSchedule = async (id, name) => {
        const result = await swal.fire({
            title: "¿Estás seguro?",
            text: `¿Eliminar el horario de ${name}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#16a34a",
            cancelButtonColor: "#dc2626",
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar",
        });
        if (result.isConfirmed) {
            await deleteScheduleApi(id);
            loadSchedules();
            swal.fire({
                title: "Eliminado!",
                text: "El horario ha sido eliminado.",
                icon: "success",
                timer: 1500,
                showConfirmButton: false,
            });
        }
    };

    const handleEdit = (schedule) => {
        setEditingSchedule(schedule);
        setShowModal(true);
    };

    const handleNewSchedule = () => {
        setEditingSchedule(null);
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setEditingSchedule(null);
    };

    const handleSuccess = () => {
        loadSchedules();
        handleModalClose();
    };

    const thBase =
        "px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300";
    const tdBase =
        "px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle";

    return (
        <>
            {/* Page header */}
            <section className="mb-6">
                <div className="max-w-full px-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                            Gestión de Horarios
                        </h1>
                        <nav aria-label="breadcrumb">
                            <ol className="flex items-center gap-2 text-sm">
                                <li className="text-blue-600 dark:text-blue-400">
                                    RR.HH.
                                </li>
                                <li className="text-gray-400 dark:text-gray-500 select-none">
                                    /
                                </li>
                                <li className="text-gray-700 dark:text-gray-300 font-medium">
                                    Horarios
                                </li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </section>

            {/* Main content */}
            <section>
                <div className="max-w-full px-4 space-y-4">
                    {/* Add Schedule Button & Header Card */}
                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div>
                                <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                    <i className="fas fa-clock text-blue-500"></i>
                                    Lista de Horarios
                                </h2>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {schedules.length} horarios
                                </p>
                            </div>
                            <button
                                onClick={handleNewSchedule}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-medium px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                            >
                                <i className="fas fa-plus"></i>
                                Nuevo Horario
                            </button>
                        </div>

                        {/* Schedules Table / List */}
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-16 gap-3">
                                <div className="h-10 w-10 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin"></div>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                    Cargando horarios...
                                </p>
                            </div>
                        ) : schedules.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 gap-3">
                                <i className="fas fa-clock text-gray-300 dark:text-gray-600 text-5xl"></i>
                                <p className="text-base font-semibold text-gray-600 dark:text-gray-400">
                                    No hay horarios registrados
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                    Comienza agregando tu primer horario
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Desktop Table */}
                                <div className="hidden md:block overflow-x-auto bg-white dark:bg-gray-800">
                                    <table className="min-w-full text-sm border-separate border-spacing-0">
                                        <thead className="sticky top-0 z-10">
                                            <tr>
                                                <th className={thBase}>Rol</th>
                                                <th className={thBase}>
                                                    Hora Inicio
                                                </th>
                                                <th className={thBase}>
                                                    Hora Fin
                                                </th>
                                                <th className={thBase}>
                                                    Días de la Semana
                                                </th>
                                                <th className={thBase}>
                                                    Con Restricciones
                                                </th>
                                                <th className={thBase}>
                                                    Acciones
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {schedules.map((sch) => (
                                                <tr
                                                    key={sch.scheduleId}
                                                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                                >
                                                    <td className={tdBase}>
                                                        <div className="flex items-center justify-center gap-2">
                                                            <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                                                                <i className="fas fa-user-tie"></i>
                                                            </div>
                                                            <span className="font-semibold text-gray-700 dark:text-gray-300">
                                                                {sch.roleName}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className={tdBase}>
                                                        <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium">
                                                            {sch.startTime ||
                                                                "-"}
                                                        </span>
                                                    </td>
                                                    <td className={tdBase}>
                                                        <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium">
                                                            {sch.endTime || "-"}
                                                        </span>
                                                    </td>
                                                    <td className={tdBase}>
                                                        <span className="text-gray-600 dark:text-gray-400">
                                                            {sch.daysOfWeek}
                                                        </span>
                                                    </td>
                                                    <td className={tdBase}>
                                                        {sch.hasRestrictions ? (
                                                            <span className="px-2 py-1 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-medium">
                                                                Sí
                                                            </span>
                                                        ) : (
                                                            <span className="px-2 py-1 rounded-md bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium">
                                                                No
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className={tdBase}>
                                                        <div className="flex items-center justify-center gap-1">
                                                            <button
                                                                onClick={() =>
                                                                    handleEdit(
                                                                        sch,
                                                                    )
                                                                }
                                                                className="inline-flex items-center gap-1 rounded-md border border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-xs font-medium px-2 py-1 transition-colors"
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                                Editar
                                                            </button>
                                                            <button
                                                                onClick={() =>
                                                                    deleteSchedule(
                                                                        sch.scheduleId,
                                                                        sch.roleName,
                                                                    )
                                                                }
                                                                className="inline-flex items-center gap-1 rounded-md border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs font-medium px-2 py-1 transition-colors"
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                                Eliminar
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Cards */}
                                <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700">
                                    {schedules.map((sch) => (
                                        <div
                                            key={sch.scheduleId}
                                            className="p-4"
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                                                        <i className="fas fa-user-tie"></i>
                                                    </div>
                                                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                                        {sch.roleName}
                                                    </p>
                                                </div>
                                                {sch.hasRestrictions ? (
                                                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                                                        Con restricciones
                                                    </span>
                                                ) : (
                                                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                                        Sin restricciones
                                                    </span>
                                                )}
                                            </div>

                                            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-1.5">
                                                    <p className="text-gray-500 dark:text-gray-400 mb-1">
                                                        Hora Inicio
                                                    </p>
                                                    <p className="font-semibold text-gray-700 dark:text-gray-300">
                                                        {sch.startTime || "-"}
                                                    </p>
                                                </div>
                                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-1.5">
                                                    <p className="text-gray-500 dark:text-gray-400 mb-1">
                                                        Hora Fin
                                                    </p>
                                                    <p className="font-semibold text-gray-700 dark:text-gray-300">
                                                        {sch.endTime || "-"}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-1.5 mb-3">
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">
                                                    Días de la Semana
                                                </p>
                                                <p className="font-semibold text-gray-700 dark:text-gray-300">
                                                    {sch.daysOfWeek}
                                                </p>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() =>
                                                        handleEdit(sch)
                                                    }
                                                    className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg border border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-sm font-medium px-3 py-2 transition-colors"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                    Editar
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        deleteSchedule(
                                                            sch.scheduleId,
                                                            sch.roleName,
                                                        )
                                                    }
                                                    className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm font-medium px-3 py-2 transition-colors"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                    Eliminar
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </section>

            {/* Modal */}
            <WidgetModal
                isOpen={showModal}
                onClose={handleModalClose}
                title={editingSchedule ? "Editar Horario" : "Nuevo Horario"}
                size="lg"
            >
                <FragmentScheduleForm
                    schedule={editingSchedule}
                    onSuccess={handleSuccess}
                />
            </WidgetModal>
        </>
    );
}
